import type { IntegrationDefinition } from "./IntegrationsCatalogShared";
import { textField, tokenField } from "./IntegrationsCatalogShared";

export const catalogPart1: IntegrationDefinition[] = [
  {
    kind: "mailbox",
    label: "Email mailbox",
    group: "Messaging",
    description: "Read incoming email and send replies from a mailbox.",
    setup:
      "You’ll need the mailbox login and the incoming/outgoing mail server details from your email provider.",
    capabilities: [
      { key: "receive_events", label: "Read new email" },
      { key: "send_messages", label: "Send replies" },
    ],
    fields: [
      textField(
        "imapHost",
        "Incoming mail server",
        "Where Lumen checks for new messages.",
        { required: true, placeholder: "imap.example.com" },
      ),
      textField("imapPort", "Incoming port", "Usually 993 for secure IMAP.", {
        type: "number",
        required: true,
        defaultValue: "993",
      }),
      textField(
        "username",
        "Mailbox username",
        "Usually the full email address.",
        { required: true, placeholder: "you@example.com" },
      ),
      textField(
        "password",
        "Mailbox password",
        "The password or app password for this mailbox.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste password",
        },
      ),
      textField(
        "smtpHost",
        "Outgoing mail server",
        "Where Lumen sends replies.",
        { required: true, placeholder: "smtp.example.com" },
      ),
      textField("smtpPort", "Outgoing port", "Usually 587 for STARTTLS.", {
        type: "number",
        required: true,
        defaultValue: "587",
      }),
      textField(
        "smtpUsername",
        "Outgoing username",
        "Leave blank only if your provider does not require one.",
        { placeholder: "Usually your email address" },
      ),
      textField(
        "smtpPassword",
        "Outgoing password",
        "Leave blank only if your provider does not require one.",
        { type: "password", secret: true, placeholder: "Paste password" },
      ),
      textField(
        "from",
        "Reply from address",
        "The address people will see on replies.",
        { type: "email", required: true, placeholder: "you@example.com" },
      ),
      textField(
        "folder",
        "Inbox folder",
        "The folder to monitor for new messages.",
        { required: true, defaultValue: "INBOX" },
      ),
    ],
  },
  {
    kind: "whatsapp",
    label: "WhatsApp Business",
    group: "Messaging",
    description: "Receive WhatsApp messages and reply with text or media.",
    setup:
      "Create a WhatsApp Business app in Meta. You’ll need its permanent access token, phone number ID, and verify token.",
    capabilities: [
      { key: "receive_events", label: "Receive messages" },
      { key: "send_messages", label: "Reply to messages" },
      { key: "send_media", label: "Send media" },
    ],
    fields: [
      tokenField(
        "Permanent access token",
        "The Meta token that lets Lumen send WhatsApp messages.",
      ),
      textField(
        "phoneNumberId",
        "WhatsApp phone number ID",
        "Found in Meta Business Suite under your WhatsApp phone number.",
        { required: true, placeholder: "e.g. 123456789012345" },
      ),
      textField(
        "verifyToken",
        "Webhook verification token",
        "Choose any private phrase. You’ll use the same phrase when setting up Meta’s webhook.",
        {
          secret: true,
          required: true,
          placeholder: "Create a private phrase",
        },
      ),
      textField(
        "appSecret",
        "App secret",
        "Optional extra signature verification for incoming messages.",
        {
          type: "password",
          secret: true,
          placeholder: "Paste Meta app secret",
        },
      ),
      textField(
        "recipient",
        "Default recipient",
        "Optional. Used when an automation sends a message without a recipient.",
        { placeholder: "Phone number with country code" },
      ),
    ],
  },
  {
    kind: "telegram",
    label: "Telegram bot",
    group: "Messaging",
    description:
      "Let automations receive Telegram messages and reply through a bot.",
    setup:
      "Create a bot with BotFather. The bot token is the only required credential; a webhook secret and default chat are optional.",
    capabilities: [
      { key: "receive_events", label: "Receive messages" },
      { key: "send_messages", label: "Reply to chats" },
      { key: "send_media", label: "Send media" },
    ],
    fields: [
      textField(
        "botToken",
        "Bot token",
        "The token BotFather gave you. Keep it private.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "123456:ABC-…",
        },
      ),
      textField(
        "webhookSecret",
        "Webhook secret",
        "Optional. A private value used to verify Telegram webhook requests.",
        {
          type: "password",
          secret: true,
          placeholder: "Optional private phrase",
        },
      ),
      textField(
        "recipient",
        "Default chat",
        "Optional. Use a chat ID when an automation has no recipient.",
        { placeholder: "e.g. -1001234567890" },
      ),
    ],
  },
  {
    kind: "slack",
    label: "Slack",
    group: "Messaging",
    description: "Listen for Slack events and reply in channels or threads.",
    setup:
      "Create a Slack app and install it in your workspace. You’ll need its bot token and signing secret.",
    capabilities: [
      { key: "receive_events", label: "Receive events" },
      { key: "send_messages", label: "Reply in Slack" },
    ],
    fields: [
      tokenField(
        "Bot token",
        "The Slack token starting with xoxb- that lets Lumen post messages.",
      ),
      textField(
        "signingSecret",
        "Signing secret",
        "Slack uses this to prove incoming events came from your workspace.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste Slack signing secret",
        },
      ),
      textField(
        "channelId",
        "Default channel",
        "Optional. Used when an automation does not specify a channel.",
        { placeholder: "e.g. C0123456789" },
      ),
    ],
  },
  {
    kind: "teams",
    label: "Microsoft Teams",
    group: "Messaging",
    description: "Send notifications to a Teams channel from automations.",
    setup:
      "Create an incoming webhook workflow in the Teams channel where messages should appear.",
    capabilities: [{ key: "send_messages", label: "Send notifications" }],
    fields: [
      textField(
        "webhookUrl",
        "Teams workflow URL",
        "The HTTPS URL generated by your Teams incoming webhook workflow.",
        { type: "url", required: true, placeholder: "https://…" },
      ),
    ],
  },
  {
    kind: "sms",
    label: "SMS",
    group: "Messaging",
    description: "Receive and send SMS messages through Twilio.",
    setup:
      "Create a Twilio account and phone number. You’ll need the Account SID, Auth Token, and sending phone number.",
    capabilities: [
      { key: "receive_events", label: "Receive SMS" },
      { key: "send_messages", label: "Send SMS" },
    ],
    fields: [
      textField(
        "accountSid",
        "Twilio Account SID",
        "Your Twilio account identifier.",
        { required: true, placeholder: "AC…" },
      ),
      textField(
        "authToken",
        "Twilio Auth Token",
        "The private credential for your Twilio account.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste Auth Token",
        },
      ),
      textField(
        "fromNumber",
        "Twilio phone number",
        "The Twilio number messages are sent from, including country code.",
        { required: true, placeholder: "+1 555 123 4567" },
      ),
    ],
  },
  {
    kind: "voice",
    label: "Voice calls",
    group: "Messaging",
    description:
      "Place Twilio voice calls with generated speech from automations.",
    setup:
      "Create a Twilio account and phone number that can make voice calls.",
    capabilities: [{ key: "send_messages", label: "Place calls" }],
    fields: [
      textField(
        "accountSid",
        "Twilio Account SID",
        "Your Twilio account identifier.",
        { required: true, placeholder: "AC…" },
      ),
      textField(
        "authToken",
        "Twilio Auth Token",
        "The private credential for your Twilio account.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste Auth Token",
        },
      ),
      textField(
        "fromNumber",
        "Calling phone number",
        "The Twilio number calls are placed from, including country code.",
        { required: true, placeholder: "+1 555 123 4567" },
      ),
    ],
  },
  {
    kind: "webhook",
    label: "Webhook",
    group: "Messaging",
    description: "Connect an automation tool or internal service using HTTP.",
    setup:
      "Paste the HTTPS endpoint that should receive messages. This works with n8n, Make, Zapier, and custom services.",
    capabilities: [
      { key: "receive_events", label: "Receive events" },
      { key: "send_messages", label: "Send webhook payloads" },
    ],
    fields: [
      textField(
        "targetUrl",
        "Destination URL",
        "The HTTPS endpoint Lumen should call.",
        {
          type: "url",
          required: true,
          placeholder: "https://hooks.example.com/…",
        },
      ),
      textField(
        "signingSecret",
        "Signing secret",
        "Required. Lumen uses this with a five-minute timestamp window to verify requests from your service.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Create a private signing secret",
        },
      ),
    ],
  },
  {
    kind: "google_calendar",
    label: "Google Calendar",
    group: "Productivity",
    description:
      "Give automations access to calendars, events, and scheduling context.",
    setup:
      "Create a Google OAuth access token with Calendar permission, then paste it here.",
    capabilities: [{ key: "calendar", label: "Calendar actions" }],
    fields: [
      tokenField(
        "Google access token",
        "A Google token with access to the calendars Lumen should use.",
      ),
    ],
  },
  {
    kind: "microsoft_calendar",
    label: "Microsoft Calendar",
    group: "Productivity",
    description:
      "Work with Outlook calendars and schedule meetings through Microsoft Graph.",
    setup:
      "Create a Microsoft Entra app token with calendar permissions for the account you want to connect.",
    capabilities: [{ key: "calendar", label: "Calendar actions" }],
    fields: [
      tokenField(
        "Microsoft Graph access token",
        "A token with calendar permission for the connected Microsoft account.",
      ),
    ],
  },
  {
    kind: "google_drive",
    label: "Google Drive",
    group: "Knowledge",
    description:
      "Use permission-aware Drive files as context for conversations and automations.",
    setup: "Create a Google OAuth access token with Drive read permission.",
    capabilities: [{ key: "files", label: "Search Drive files" }],
    fields: [
      tokenField(
        "Google access token",
        "A token with access to the Drive files Lumen should be able to read.",
      ),
    ],
  },
  {
    kind: "webdav",
    label: "WebDAV",
    group: "Knowledge",
    description: "Upload artifacts to any WebDAV-compatible storage.",
    setup:
      "Provide the WebDAV endpoint and credentials for the destination folder.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      textField("baseUrl", "WebDAV URL", "The HTTPS WebDAV collection URL.", {
        type: "url",
        required: true,
      }),
      textField("username", "Username", "WebDAV account username.", {
        required: true,
      }),
      textField("password", "Password", "WebDAV account password.", {
        type: "password",
        secret: true,
        required: true,
      }),
      textField(
        "remotePath",
        "Remote folder",
        "Optional folder below the WebDAV URL.",
      ),
    ],
  },
  {
    kind: "nextcloud",
    label: "Nextcloud",
    group: "Knowledge",
    description: "Upload artifacts to a Nextcloud files account.",
    setup:
      "Provide your Nextcloud server URL, username, app password, and optional folder.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      textField(
        "baseUrl",
        "Nextcloud WebDAV URL",
        "For example https://cloud.example.com/remote.php/dav/files/user.",
        { type: "url", required: true },
      ),
      textField("username", "Username", "Nextcloud username.", {
        required: true,
      }),
      textField("password", "App password", "Use a Nextcloud app password.", {
        type: "password",
        secret: true,
        required: true,
      }),
      textField(
        "remotePath",
        "Remote folder",
        "Optional folder below the WebDAV URL.",
      ),
    ],
  },
  {
    kind: "samba",
    label: "Samba share",
    group: "Knowledge",
    description: "Upload artifacts to an SMB/CIFS network share.",
    setup:
      "The API host must have smbclient installed and be able to reach the share.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      textField("share", "SMB share", "For example //server/share.", {
        required: true,
      }),
      textField("username", "Username", "SMB account username.", {
        required: true,
      }),
      textField("password", "Password", "SMB account password.", {
        type: "password",
        secret: true,
        required: true,
      }),
      textField("domain", "Domain", "Optional Windows domain."),
      textField(
        "remotePath",
        "Remote folder",
        "Optional folder inside the share.",
      ),
    ],
  },
  {
    kind: "onedrive",
    label: "OneDrive",
    group: "Knowledge",
    description: "Upload artifacts to a Microsoft OneDrive account.",
    setup:
      "Create a Microsoft Graph access token with Files.ReadWrite permission.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      tokenField(
        "Microsoft Graph access token",
        "A token with OneDrive file write permission.",
      ),
      textField(
        "folderId",
        "Folder ID",
        "Optional OneDrive folder ID; blank uploads to the root.",
      ),
    ],
  },
  {
    kind: "dropbox",
    label: "Dropbox",
    group: "Knowledge",
    description: "Upload artifacts to Dropbox.",
    setup: "Create a Dropbox access token with files.content.write permission.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      tokenField(
        "Dropbox access token",
        "A token with permission to upload files.",
      ),
      textField("remotePath", "Remote folder", "Optional Dropbox folder."),
    ],
  },
  {
    kind: "box",
    label: "Box",
    group: "Knowledge",
    description: "Upload artifacts to Box.",
    setup:
      "Create a Box developer token or OAuth access token with upload permission.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      tokenField(
        "Box access token",
        "A token with permission to upload files.",
      ),
      textField(
        "folderId",
        "Folder ID",
        "Optional Box folder ID; blank uploads to All Files.",
      ),
    ],
  },
  {
    kind: "s3",
    label: "Amazon S3",
    group: "Knowledge",
    description:
      "Upload artifacts to Amazon S3 or an S3-compatible object store.",
    setup:
      "Provide a bucket, region, endpoint, and access credentials. Custom endpoints support MinIO and other S3-compatible services.",
    capabilities: [{ key: "files", label: "Upload artifacts" }],
    fields: [
      textField(
        "endpoint",
        "S3 endpoint",
        "For example https://s3.amazonaws.com or a MinIO endpoint.",
        { type: "url", required: true },
      ),
      textField("bucket", "Bucket", "The target bucket.", { required: true }),
      textField(
        "region",
        "Region",
        "AWS region or the region configured by your S3-compatible server.",
        { required: true, defaultValue: "us-east-1" },
      ),
      textField("accessKeyId", "Access key ID", "S3 access key ID.", {
        required: true,
      }),
      textField(
        "secretAccessKey",
        "Secret access key",
        "S3 secret access key.",
        { type: "password", secret: true, required: true },
      ),
      textField("remotePath", "Prefix", "Optional object-key prefix."),
    ],
  },
  {
    kind: "sharepoint",
    label: "SharePoint",
    group: "Knowledge",
    description:
      "Use SharePoint sites and documents as permission-aware context.",
    setup:
      "Create a Microsoft Graph token with permission to read the SharePoint sites you want to use.",
    capabilities: [{ key: "files", label: "Search SharePoint files" }],
    fields: [
      tokenField(
        "Microsoft Graph access token",
        "A token with access to the SharePoint sites Lumen should be able to read.",
      ),
    ],
  },
  {
    kind: "notion",
    label: "Notion",
    group: "Knowledge",
    description:
      "Search connected Notion pages and databases for relevant context.",
    setup:
      "Create a Notion internal integration, then share the pages or databases it should be able to read.",
    capabilities: [{ key: "files", label: "Search Notion content" }],
    fields: [
      tokenField(
        "Notion integration token",
        "The internal integration token from Notion. Make sure the target pages are shared with it.",
      ),
    ],
  },
  {
    kind: "hubspot",
    label: "HubSpot",
    group: "Operations",
    description: "Read and update contacts, companies, and CRM records.",
    setup:
      "Create a HubSpot private app with the CRM permissions your automations need.",
    capabilities: [{ key: "crm", label: "CRM actions" }],
    fields: [
      tokenField(
        "Private app access token",
        "The token from your HubSpot private app.",
      ),
    ],
  },
  {
    kind: "zendesk",
    label: "Zendesk",
    group: "Operations",
    description: "Bring support tickets and customer context into automations.",
    setup:
      "Create a Zendesk API token and enter your help center’s HTTPS address.",
    capabilities: [{ key: "tickets", label: "Support ticket context" }],
    fields: [
      tokenField(
        "Zendesk API token",
        "The API token from your Zendesk admin center.",
      ),
      textField(
        "baseUrl",
        "Zendesk account URL",
        "Your Zendesk subdomain, for example https://acme.zendesk.com.",
        {
          type: "url",
          required: true,
          placeholder: "https://your-company.zendesk.com",
        },
      ),
    ],
  },
  {
    kind: "github",
    label: "GitHub",
    group: "Developer",
    description:
      "Work with issues, pull requests, and repositories in automations.",
    setup:
      "Create a GitHub personal access token with access to the repositories you want to connect. GitHub.com is filled in automatically.",
    capabilities: [{ key: "repositories", label: "Repository actions" }],
    fields: [
      tokenField(
        "Personal access token",
        "A GitHub token with the repository permissions your automations need.",
      ),
      textField(
        "baseUrl",
        "GitHub server URL",
        "Only change this if you use GitHub Enterprise.",
        { type: "url", defaultValue: "https://api.github.com", advanced: true },
      ),
    ],
  },
  {
    kind: "gitlab",
    label: "GitLab",
    group: "Developer",
    description:
      "Work with issues, merge requests, and repositories in automations.",
    setup:
      "Create a GitLab personal access token. GitLab.com is filled in automatically.",
    capabilities: [{ key: "repositories", label: "Repository actions" }],
    fields: [
      tokenField(
        "Personal access token",
        "A GitLab token with the project permissions your automations need.",
      ),
      textField(
        "baseUrl",
        "GitLab API URL",
        "Only change this if you use a self-managed GitLab server.",
        {
          type: "url",
          defaultValue: "https://gitlab.com/api/v4",
          advanced: true,
        },
      ),
    ],
  },
  {
    kind: "codeberg",
    label: "Codeberg",
    group: "Developer",
    description: "Work with Codeberg issues and repositories in automations.",
    setup:
      "Create a Codeberg access token. Codeberg.org is filled in automatically.",
    capabilities: [{ key: "repositories", label: "Repository actions" }],
    fields: [
      tokenField(
        "Access token",
        "A Codeberg token with access to the repositories your automations need.",
      ),
      textField(
        "baseUrl",
        "Codeberg API URL",
        "Only change this if you use another Forgejo server.",
        {
          type: "url",
          defaultValue: "https://codeberg.org/api/v1",
          advanced: true,
        },
      ),
    ],
  },
  {
    kind: "gitea",
    label: "Self-hosted Gitea",
    group: "Developer",
    description:
      "Work with repositories and issues in a local or self-hosted Gitea instance.",
    setup:
      "Create a Gitea access token with repository and issue permissions. The default points at a Docker Compose service named gitea; replace it with your internal URL when needed.",
    capabilities: [{ key: "repositories", label: "Repository actions" }],
    fields: [
      tokenField(
        "Gitea access token",
        "A Gitea token with access to the repositories and issues Lumen should use.",
      ),
      textField(
        "baseUrl",
        "Gitea API URL",
        "The Gitea API base URL. HTTP is allowed for explicitly local hosts.",
        {
          type: "url",
          defaultValue: "http://gitea:3000/api/v1",
          required: true,
        },
      ),
      textField(
        "sshCloneBaseUrl",
        "SSH clone base URL (optional)",
        "Use this when the web/API address differs from the SSH address, for example ssh://git@gitea:22.",
        {
          type: "url",
          advanced: true,
          placeholder: "ssh://git@gitea:22",
        },
      ),
    ],
  },
  {
    kind: "google_play_console",
    label: "Google Play Console",
    group: "Developer",
    description:
      "Inspect Android reviews and subscriptions through the Google Play Developer API.",
    setup:
      "Use a service-account JSON key with Google Play Developer API access. For short-lived environments you may provide an OAuth access token instead.",
    capabilities: [{ key: "repositories", label: "App management" }],
    fields: [
      textField(
        "serviceAccountJson",
        "Service-account JSON",
        "Paste the complete Google service-account JSON key. Keep the key scoped to the Play Console account.",
        {
          secret: true,
          multiline: true,
          required: true,
          placeholder: '{"type":"service_account",…}',
        },
      ),
      textField(
        "packageName",
        "Android package name",
        "The application ID registered in Play Console.",
        { required: true, placeholder: "com.example.app" },
      ),
    ],
  },
  {
    kind: "app_store_connect",
    label: "App Store Connect",
    group: "Developer",
    description:
      "Inspect iOS apps, TestFlight builds, and customer reviews through Apple’s API.",
    setup:
      "Create an App Store Connect API key. Paste the issuer ID, key ID, and the private key in PEM format.",
    capabilities: [{ key: "repositories", label: "App management" }],
    fields: [
      textField(
        "issuerId",
        "Issuer ID",
        "The issuer ID shown in App Store Connect API access.",
        { required: true, placeholder: "Issuer UUID" },
      ),
      textField(
        "keyId",
        "Key ID",
        "The key ID for the App Store Connect API key.",
        { required: true, placeholder: "10-character key ID" },
      ),
      textField(
        "privateKey",
        "Private key",
        "The ES256 private key in PEM format. Literal \\n sequences are also accepted.",
        {
          secret: true,
          multiline: true,
          required: true,
          placeholder: "-----BEGIN PRIVATE KEY-----",
        },
      ),
      textField(
        "bundleId",
        "Bundle ID",
        "Used when testing the connection against your app.",
        { required: true, placeholder: "com.example.app" },
      ),
    ],
  },
  {
    kind: "facebook_pages",
    label: "Facebook Pages",
    group: "Marketing",
    description: "Publish approved Page content and read Page insights.",
    setup:
      "Create a Meta token with access to the Page, then enter the Page ID you want to use.",
    capabilities: [{ key: "social", label: "Publish Page content" }],
    fields: [
      tokenField(
        "Page access token",
        "The Meta token for the Page Lumen should publish to.",
      ),
      textField(
        "accountId",
        "Facebook Page ID",
        "The numeric ID of the Page, not your personal profile ID.",
        { required: true, placeholder: "e.g. 123456789012345" },
      ),
    ],
  },
  {
    kind: "instagram_business",
    label: "Instagram Business",
    group: "Marketing",
    description:
      "Publish approved images and read Instagram Business insights.",
    setup:
      "Connect an Instagram Business account through Meta and create a token with the required publishing permissions.",
    capabilities: [{ key: "social", label: "Publish Instagram content" }],
    fields: [
      tokenField(
        "Meta access token",
        "The Meta token for the Instagram Business account.",
      ),
      textField(
        "accountId",
        "Instagram Business account ID",
        "The account ID from Meta Business Suite.",
        { required: true, placeholder: "e.g. 17841400000000000" },
      ),
    ],
  },
  {
    kind: "linkedin",
    label: "LinkedIn",
    group: "Marketing",
    description: "Publish approved posts for a LinkedIn organization.",
    setup:
      "Create a LinkedIn token with organization posting permission and identify the organization to publish for.",
    capabilities: [{ key: "social", label: "Publish organization posts" }],
    fields: [
      tokenField(
        "LinkedIn access token",
        "The token with permission to publish for your organization.",
      ),
      textField(
        "accountId",
        "LinkedIn organization ID",
        "The numeric organization ID, not the public profile URL.",
        { required: true, placeholder: "e.g. 12345678" },
      ),
    ],
  },
  {
    kind: "tiktok",
    label: "TikTok",
    group: "Marketing",
    description: "Prepare approved video uploads for TikTok.",
    setup:
      "Create a TikTok developer app and token with the content publishing permissions you need.",
    capabilities: [{ key: "social", label: "Prepare video uploads" }],
    fields: [
      tokenField(
        "TikTok access token",
        "The token for the TikTok account or app you want to connect.",
      ),
    ],
  },
  {
    kind: "x",
    label: "X",
    group: "Marketing",
    description: "Publish approved posts and read account details on X.",
    setup:
      "Create an X developer app and token with write access if you want to publish posts.",
    capabilities: [{ key: "social", label: "Publish posts" }],
    fields: [
      tokenField(
        "X access token",
        "The token for the X account or app you want to connect.",
      ),
    ],
  },
  {
    kind: "youtube",
    label: "YouTube",
    group: "Marketing",
    description: "Read channel and video reporting context.",
    setup:
      "Create a Google OAuth token with permission to read the YouTube channel you want to connect.",
    capabilities: [{ key: "social", label: "Read channel reporting" }],
    fields: [
      tokenField(
        "Google / YouTube access token",
        "A Google token with access to the channel data Lumen should read.",
      ),
    ],
  },
  {
    kind: "email_marketing",
    label: "Email marketing",
    group: "Marketing",
    description: "Send approved campaigns and manage contacts through Resend.",
    setup:
      "Create a Resend API key. The provider is already set to Resend; add a sender address and audience only if your campaigns need them.",
    capabilities: [{ key: "marketing_email", label: "Send campaigns" }],
    fields: [
      textField(
        "apiKey",
        "Resend API key",
        "The private API key from Resend.",
        { type: "password", secret: true, required: true, placeholder: "re_…" },
      ),
      textField(
        "fromEmail",
        "Default sender address",
        "Optional. Used when a campaign does not specify a sender.",
        { type: "email", placeholder: "hello@example.com" },
      ),
      textField(
        "audienceId",
        "Default audience ID",
        "Optional. Used when a campaign does not specify an audience.",
        { placeholder: "Audience ID" },
      ),
    ],
  },
  {
    kind: "google_analytics",
    label: "Google Analytics",
    group: "Analytics",
    description: "Run GA4 reports for campaign and product analysis.",
    setup:
      "Create a Google token with Analytics read permission and copy the GA4 property ID.",
    capabilities: [{ key: "analytics", label: "Run GA4 reports" }],
    fields: [
      tokenField(
        "Google access token",
        "A token with permission to read the GA4 property.",
      ),
      textField(
        "propertyId",
        "GA4 property ID",
        "The numeric property ID found in Google Analytics settings.",
        { required: true, placeholder: "e.g. 123456789" },
      ),
    ],
  },
  {
    kind: "google_search_console",
    label: "Google Search Console",
    group: "Analytics",
    description: "Query search traffic, queries, and SEO performance.",
    setup:
      "Create a Google token with Search Console read permission and enter the verified site URL.",
    capabilities: [{ key: "analytics", label: "Query SEO performance" }],
    fields: [
      tokenField(
        "Google access token",
        "A token with permission to read the Search Console property.",
      ),
      textField(
        "siteUrl",
        "Verified site URL",
        "Enter the exact URL or domain property shown in Search Console.",
        { type: "url", required: true, placeholder: "https://example.com/" },
      ),
    ],
  },
  {
    kind: "restolution",
    label: "Restolution",
    group: "Restaurant operations",
    description:
      "Connect Finnish restaurant POS menus, receipts, payments, and sales data.",
    setup:
      "Create Restolution API credentials and enter the restaurant API endpoint. API access may require a Restolution partner agreement.",
    capabilities: [
      { key: "pos", label: "POS data" },
      { key: "orders", label: "Menus & receipts" },
      { key: "accounting", label: "Sales data" },
    ],
    fields: [
      textField(
        "apiKey",
        "Restolution API key",
        "The API credential provided by Restolution.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste API key",
        },
      ),
      textField(
        "secret",
        "Restolution API secret",
        "The secret paired with the Restolution API key for Basic authentication.",
        {
          type: "password",
          secret: true,
          required: true,
          placeholder: "Paste API secret",
        },
      ),
      textField(
        "baseUrl",
        "Restolution API URL",
        "The JSON API endpoint for the connected restaurant.",
        {
          type: "url",
          required: true,
          defaultValue: "https://restolution.fi/resto/api",
        },
      ),
      textField(
        "restaurantId",
        "Restaurant ID",
        "The Restolution business-unit or restaurant identifier.",
        { required: true, placeholder: "Restaurant ID" },
      ),
    ],
  },
];
